// src/components/ItemizedDeductions.js
import React, { useEffect, useMemo, useState } from 'react';
import { List, Link, Info, ChevronDown, ChevronRight, AlertCircle, HelpCircle, UserCheck } from 'react-feather';
import { getTaxConstants } from '../taxConstants';

function ItemizedDeductions({ 
  data, 
  formData, // <--- Receiving full formData to access manual overrides
  expensesData = [], 
  onDataChange, 
  agi, 
  taxYear,
  standardDeduction = 0,
  totalItemized = 0,
  deductionType = 'Standard',
  investmentIncome = 0
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasItemizedData = (
    (parseFloat(formData.manualMedicalExpenses) || 0) > 0 ||
    (parseFloat(formData.manualStateTaxes) || 0) > 0 ||
    (parseFloat(formData.manualRealEstateTaxes) || 0) > 0 ||
    (parseFloat(formData.manualMortgageInterest) || 0) > 0 ||
    (parseFloat(formData.manualCharityCash) || 0) > 0
    
  );

  const [showItemized, setShowItemized] = useState(hasItemizedData);
  const { filingStatus } = formData; // <--- ADD THIS
  const showSpouseQuestions = filingStatus === 'Married Filing Jointly' || filingStatus === 'Married Filing Separately'; // <--- ADD THIS

  // --- 1. CALCULATE TOTALS FROM EXPENSES GRID ---
  const gridTotals = useMemo(() => {
    const totals = {
      medical: 0,
      stateTaxes: 0,
      realEstate: 0,
      personalProp: 0,
      otherTaxes: 0,
      mortgage: 0,
      mortgageNon1098: 0,
      points: 0,
      mortgageIns: 0,
      investInterest: 0,
      charityCash: 0,
      charityGoods: 0,
      casualty: 0,
      otherDed: 0
    };

    expensesData.forEach(item => {
      const amount = parseFloat(item.amount) || 0;
      if (item.isDeductible) { // Only count if marked deductible
        switch (item.category) {
            case 'Medical': totals.medical += amount; break;
            case 'State Taxes': totals.stateTaxes += amount; break;
            case 'Real Estate Taxes': totals.realEstate += amount; break;
            case 'Personal Property Taxes': totals.personalProp += amount; break;
            case 'Other Taxes': totals.otherTaxes += amount; break;
            case 'Mortgage Interest': totals.mortgage += amount; break;
            case 'Mortgage Interest (Non-1098)': totals.mortgageNon1098 += amount; break;
            case 'Mortgage Points': totals.points += amount; break;
            case 'Mortgage Insurance': totals.mortgageIns += amount; break;
            case 'Investment Interest': totals.investInterest += amount; break;
            case 'Charity (Cash)': totals.charityCash += amount; break;
            case 'Charity (Goods)': totals.charityGoods += amount; break;
            case 'Casualty Loss': totals.casualty += amount; break;
            case 'Other Deductions': totals.otherDed += amount; break;
            default: break;
        }
      }
    });
    return totals;
  }, [expensesData]);

  // --- 2. SYNC LOGIC: MANUAL OVERRIDE > GRID TOTAL ---
  useEffect(() => {
    // Helper: Look in formData (root) for manual overrides. 
    // If a manual value exists (even 0), use it. Otherwise use the grid total.
    const resolve = (manualKey, gridVal) => {
        const manual = parseFloat(formData?.[manualKey]);
        return !isNaN(manual) ? manual : gridVal;
    };

    // Medical
    const finalMedical = resolve('manualMedicalExpenses', gridTotals.medical);
    if (parseFloat(data.medicalExpenses || 0) !== finalMedical) {
        onDataChange({ target: { name: 'itemizedDeductions.medicalExpenses', value: finalMedical } });
    }

    // Taxes
    const finalState = resolve('manualStateTaxes', gridTotals.stateTaxes);
    if (parseFloat(data.stateAndLocalIncomeTaxes || 0) !== finalState) {
        onDataChange({ target: { name: 'itemizedDeductions.stateAndLocalIncomeTaxes', value: finalState } });
    }

    const finalRealEstate = resolve('manualRealEstateTaxes', gridTotals.realEstate);
    if (parseFloat(data.realEstateTaxes || 0) !== finalRealEstate) {
        onDataChange({ target: { name: 'itemizedDeductions.realEstateTaxes', value: finalRealEstate } });
    }

    const finalPersonalProp = resolve('manualPersonalPropertyTaxes', gridTotals.personalProp);
    if (parseFloat(data.personalPropertyTaxes || 0) !== finalPersonalProp) {
        onDataChange({ target: { name: 'itemizedDeductions.personalPropertyTaxes', value: finalPersonalProp } });
    }

    // Interest
    const finalMortgage = resolve('manualMortgageInterest', gridTotals.mortgage);
    if (parseFloat(data.mortgageInterest || 0) !== finalMortgage) {
        onDataChange({ target: { name: 'itemizedDeductions.mortgageInterest', value: finalMortgage } });
    }

    // Charity
    const finalCharityCash = resolve('manualCharityCash', gridTotals.charityCash);
    if (parseFloat(data.charitableDonationsCash || 0) !== finalCharityCash) {
        onDataChange({ target: { name: 'itemizedDeductions.charitableDonationsCash', value: finalCharityCash } });
    }

    const finalCharityGoods = resolve('manualCharityNonCash', gridTotals.charityGoods);
    if (parseFloat(data.charitableDonationsNonCash || 0) !== finalCharityGoods) {
        onDataChange({ target: { name: 'itemizedDeductions.charitableDonationsNonCash', value: finalCharityGoods } });
    }

    // Sync other fields that don't have manual overrides but come from grid
    if (parseFloat(data.otherTaxes || 0) !== gridTotals.otherTaxes) {
        onDataChange({ target: { name: 'itemizedDeductions.otherTaxes', value: gridTotals.otherTaxes } });
    }
    if (parseFloat(data.mortgageInterestNot1098 || 0) !== gridTotals.mortgageNon1098) {
        onDataChange({ target: { name: 'itemizedDeductions.mortgageInterestNot1098', value: gridTotals.mortgageNon1098 } });
    }
    // Only sync points if calculator isn't being used
    if (!data.pointsCalculator?.totalPointsPaid && parseFloat(data.pointsNot1098 || 0) !== gridTotals.points) {
         onDataChange({ target: { name: 'itemizedDeductions.pointsNot1098', value: gridTotals.points } });
    }
    if (parseFloat(data.mortgageInsurancePremiums || 0) !== gridTotals.mortgageIns) {
        onDataChange({ target: { name: 'itemizedDeductions.mortgageInsurancePremiums', value: gridTotals.mortgageIns } });
    }
    if (parseFloat(data.investmentInterest || 0) !== gridTotals.investInterest) {
        onDataChange({ target: { name: 'itemizedDeductions.investmentInterest', value: gridTotals.investInterest } });
    }

  }, [gridTotals, formData, data, onDataChange]);

  const constants = getTaxConstants(taxYear);
  const limits = constants.LIMITS;
  const medicalThreshold = agi * limits.MEDICAL_EXPENSE_THRESHOLD_PERCENT;
  const saltCap = limits.SALT_CAP;

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

  return (
    <div className="form-section">
      <div className="content-box-header">
        <h2><List size={20} className="icon-orange" /> Itemized Deductions</h2>
      </div>
      {/* --- MERGED STANDARD DEDUCTION QUESTIONS --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
        <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: '1px solid #fed7aa', paddingBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#9a3412', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <UserCheck size={18} className="icon-orange" /> Standard Deduction Qualifiers
            </h4>
        </div>
        
        <div className="deduction-questions-container">
            <div className="form-group checkbox-group">
                <input type="checkbox" id="isOver65" name="isOver65" checked={formData.isOver65} onChange={onDataChange} />
                <label htmlFor="isOver65" style={{ color: '#9a3412' }}>You were born before January 2, 1960</label>
            </div>
            <div className="form-group checkbox-group">
                <input type="checkbox" id="isBlind" name="isBlind" checked={formData.isBlind} onChange={onDataChange} />
                <label htmlFor="isBlind" style={{ color: '#9a3412' }}>You are legally blind</label>
            </div>

            {showSpouseQuestions && (
            <>
                <div className="form-group checkbox-group">
                    <input type="checkbox" id="spouseIsOver65" name="spouseIsOver65" checked={formData.spouseIsOver65} onChange={onDataChange} />
                    <label htmlFor="spouseIsOver65" style={{ color: '#9a3412' }}>Your spouse was born before January 2, 1960</label>
                </div>
                <div className="form-group checkbox-group">
                    <input type="checkbox" id="spouseIsBlind" name="spouseIsBlind" checked={formData.spouseIsBlind} onChange={onDataChange} />
                    <label htmlFor="spouseIsBlind" style={{ color: '#9a3412' }}>Your spouse is legally blind</label>
                </div>
            </>
            )}

            <div className="form-group checkbox-group">
                <input type="checkbox" id="canBeClaimedAsDependent" name="canBeClaimedAsDependent" checked={formData.canBeClaimedAsDependent} onChange={onDataChange} />
                <label htmlFor="canBeClaimedAsDependent" style={{ color: '#9a3412' }}>Someone can claim you as a dependent</label>
            </div>
            <div className="form-group checkbox-group">
                <input type="checkbox" id="isDualStatusAlien" name="isDualStatusAlien" checked={formData.isDualStatusAlien} onChange={onDataChange} />
                <label htmlFor="isDualStatusAlien" style={{ color: '#9a3412' }}>You were a dual-status alien</label>
            </div>
            {filingStatus === 'Married Filing Separately' && (
            <div className="form-group checkbox-group">
                <input type="checkbox" id="spouseItemizes" name="spouseItemizes" checked={formData.spouseItemizes} onChange={onDataChange} />
                <label htmlFor="spouseItemizes" style={{ color: '#9a3412' }}>Your spouse itemizes on a separate return</label>
            </div>
            )}
        </div>
      </div>

      <div className="deduction-comparison-note" style={{ 
          marginBottom: '2rem', 
          padding: '1.25rem', 
          backgroundColor: '#fff7ed', // Changed from Blue to Orange
          border: '1px solid #fed7aa', // Changed from Blue to Orange
          borderRadius: '0.5rem' 
      }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Info size={24} className="icon-orange" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flexGrow: 1 }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#9a3412', fontSize: '1.1rem' }}>How Schedule A Works</h4>
            <p style={{ margin: 0, fontSize: '0.95rem', color: '#c2410c', lineHeight: '1.5' }}>
              The IRS allows you to subtract the <strong>larger</strong> of your Standard Deduction or your Total Itemized Deductions. We automatically apply the one that saves you the most money.
            </p>
          </div>
        </div>
      </div>

      {/* --- QUESTION CARD --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label style={{ fontWeight: 600, color: '#553f33ff' }}>Do you want to itemize deductions?</label>
          <p style={{ fontSize: '0.9rem', color: '#8b7764ff', marginBottom: '0.75rem' }}>
            Answer <strong>Yes</strong> if you have significant expenses (Medical, Mortgage Interest, State Taxes, Charity) that might exceed your Standard Deduction.
          </p>
          <div className="radio-group">
            <input 
              type="radio" 
              id="itemize_no" 
              checked={!showItemized} 
              onChange={() => setShowItemized(false)} 
            />
            <label htmlFor="itemize_no">No (Standard Deduction)</label>

            <input 
              type="radio" 
              id="itemize_yes" 
              checked={showItemized} 
              onChange={() => setShowItemized(true)} 
            />
            <label htmlFor="itemize_yes">Yes (Enter Expenses)</label>
          </div>
        </div>
      </div>

      {/* --- CONDITIONAL ITEMIZED INPUTS --- */}
      {showItemized && (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            {/* --- 1. Medical Expenses --- */}
            <div className="dependent-card">
                <h4 style={{marginBottom: '1rem'}}>Medical & Dental Expenses</h4>
                <div className="form-group">
                <label>Total Medical Expenses</label>
                <div className="input-with-icon" style={{ position: 'relative' }}>
                    <input 
                    type="number" 
                    name="manualMedicalExpenses" 
                    value={formData?.manualMedicalExpenses ?? ''} 
                    onChange={onDataChange}
                    placeholder={`Calculated from grid: ${formatCurrency(gridTotals.medical)}`}
                    />
                </div>
                <small>Only the amount over 7.5% of your AGI (${formatCurrency(medicalThreshold)}) counts.</small>
                </div>
            </div>

            {/* --- 2. Taxes You Paid --- */}
            <div className="dependent-card" style={{marginTop: '1rem'}}>
                <h4 style={{marginBottom: '1rem'}}>Taxes You Paid (SALT)</h4>
                
                <div className="form-row">
                <div className="form-group">
                    <label>State & Local Income Taxes</label>
                    <input 
                        type="number" 
                        name="manualStateTaxes"
                        value={formData?.manualStateTaxes ?? ''} 
                        onChange={onDataChange}
                        placeholder={`Calculated: ${formatCurrency(gridTotals.stateTaxes)}`}
                    />
                </div>
                <div className="form-group">
                    <label>General Sales Tax (if electing)</label>
                    <input 
                        type="number" 
                        name="manualGeneralSalesTax" 
                        value={formData?.manualGeneralSalesTax ?? ''} 
                        onChange={onDataChange} 
                        placeholder="Enter total sales tax"
                    />
                </div>
                </div>

                <div className="form-row">
                <div className="form-group">
                    <label>Real Estate Taxes</label>
                    <input 
                        type="number" 
                        name="manualRealEstateTaxes"
                        value={formData?.manualRealEstateTaxes ?? ''} 
                        onChange={onDataChange}
                        placeholder={`Calculated: ${formatCurrency(gridTotals.realEstate)}`}
                    />
                </div>
                <div className="form-group">
                    <label>Personal Property Taxes</label>
                    <input 
                        type="number" 
                        name="manualPersonalPropertyTaxes"
                        value={formData?.manualPersonalPropertyTaxes ?? ''} 
                        onChange={onDataChange}
                        placeholder={`Calculated: ${formatCurrency(gridTotals.personalProp)}`}
                    />
                </div>
                </div>
                <div className="totals-summary-box" style={{marginTop: '1rem'}}>
                    <small>Total deduction for these taxes is capped at ${saltCap.toLocaleString()}.</small>
                </div>
            </div>

            {/* --- 3. Interest You Paid --- */}
            <div className="dependent-card" style={{marginTop: '1rem'}}>
                <h4 style={{marginBottom: '1rem'}}>Interest You Paid</h4>
                
                <div className="form-group checkbox-group" style={{ marginBottom: '1.5rem', backgroundColor: '#fff7ed', padding: '10px', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                    <input 
                        type="checkbox" 
                        id="mortgageProceedsNotUsedForHome" 
                        name="itemizedDeductions.mortgageProceedsNotUsedForHome" 
                        checked={data.mortgageProceedsNotUsedForHome || false} 
                        onChange={onDataChange} 
                    />
                    <label htmlFor="mortgageProceedsNotUsedForHome" style={{ color: '#9a3412', fontSize: '0.9rem' }}>
                        Check if you used any mortgage proceeds for purposes <strong>other</strong> than buying, building, or improving your home.
                    </label>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Mortgage Interest (Form 1098)</label>
                        <input 
                            type="number" 
                            name="manualMortgageInterest"
                            value={formData?.manualMortgageInterest ?? ''} 
                            onChange={onDataChange}
                            placeholder={`Calculated: ${formatCurrency(gridTotals.mortgage)}`}
                        />
                    </div>
                    <div className="form-group">
                        <label>Mortgage Insurance Premiums</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.mortgageInsurancePremiums"
                            value={data.mortgageInsurancePremiums ?? ''} 
                            onChange={onDataChange}
                            placeholder={`Calculated: ${formatCurrency(gridTotals.mortgageIns)}`}
                        />
                    </div>
                </div>
            </div>

            {/* --- 4. Charity --- */}
            <div className="dependent-card" style={{marginTop: '1rem'}}>
                <h4 style={{marginBottom: '1rem'}}>Gifts to Charity</h4>
                
                <div className="form-row">
                    <div className="form-group">
                        <label>Gifts by Cash or Check</label>
                        <input 
                            type="number" 
                            name="manualCharityCash" 
                            value={formData?.manualCharityCash ?? ''} 
                            onChange={onDataChange} 
                            placeholder={`Calculated: ${formatCurrency(gridTotals.charityCash)}`}
                        />
                    </div>

                    <div className="form-group">
                        <label>Gifts other than Cash (Goods)</label>
                        <input 
                            type="number" 
                            name="manualCharityNonCash" 
                            value={formData?.manualCharityNonCash ?? ''} 
                            onChange={onDataChange} 
                            placeholder={`Calculated: ${formatCurrency(gridTotals.charityGoods)}`}
                        />
                    </div>
                </div>

                <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Charity Carryover from Prior Year</label>
                    <input 
                        type="number" 
                        name="itemizedDeductions.charitableCarryover" 
                        value={data.charitableCarryover ?? ''} 
                        onChange={onDataChange} 
                        placeholder="Enter amount from prior return"
                    />
                </div>
            </div>
        </div>
      )}

      {/* --- ADVANCED TOGGLE --- */}
      <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f0eae2ff', paddingTop: '1rem' }}>
        <button 
            className="user-profile-button" 
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{ width: '100%', justifyContent: 'space-between', backgroundColor: '#fcfaf8ff', border: '1px solid #f0e8e2ff' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertCircle size={18} className="icon-blue" />
                <span style={{ fontWeight: 600, color: '#334155' }}>
                    {showAdvanced ? "Hide Less Common Deductions" : "Show Less Common Deductions (Casualty, Gambling, etc.)"}
                </span>
            </div>
            {showAdvanced ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        {showAdvanced && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fcf9f8ff', borderRadius: '0.5rem', border: '1px solid #f0eae2ff', animation: 'fadeIn 0.3s' }}>
                
                {/* 1. Complex Interest */}
                <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Complex Interest</h5>
                <div className="form-row">
                    <div className="form-group">
                        <label>Mortgage Interest NOT on Form 1098</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.mortgageInterestNot1098"
                            value={data.mortgageInterestNot1098 ?? ''} 
                            onChange={onDataChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>Points NOT on Form 1098</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.pointsNot1098"
                            value={data.pointsNot1098 ?? ''} 
                            onChange={onDataChange}
                        />
                    </div>
                </div>
                
                {/* Payee Info for 8b */}
                {(parseFloat(data.mortgageInterestNot1098) > 0) && (
                    <div className="form-group" style={{ backgroundColor: '#fffbeb', padding: '10px', border: '1px solid #fcd34d', borderRadius: '6px' }}>
                        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                            <label style={{ color: '#92400e', fontWeight: '600' }}>Recipient Information (Required for 8b)</label>
                            <div className="has-tooltip header-help-icon" data-tooltip="You must provide the Name, Address, and SSN (or EIN) of the person you paid interest to. Failure to do so may result in a $50 penalty.">
                                <HelpCircle size={16} color="#92400e" />
                            </div>
                        </div>
                        <textarea 
                            name="itemizedDeductions.mortgageInterestPayee" 
                            value={data.mortgageInterestPayee ?? ''} 
                            onChange={onDataChange} 
                            placeholder="Name: John Doe, Address: 123 Main St, SSN: 123-45-6789"
                            rows={2}
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #dbd6d1ff', fontSize: '0.95rem' }}
                        />
                        <small style={{ color: '#b45309', display: 'block', marginTop: '4px' }}>
                            Format: <strong>Name, Address, SSN/EIN</strong>
                        </small>
                    </div>
                )}

                {/* Points Smart Calculator (Line 8c) */}
                <div className="form-group" style={{ marginTop: '1rem', backgroundColor: '#fff7ed', padding: '1rem', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label style={{ color: '#9a3412', fontWeight: '600' }}>Points Not Reported on Form 1098 (Line 8c)</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Points are prepaid interest. Rules differ for purchases vs. refinances.">
                            <HelpCircle size={16} color="#9a3412" />
                        </div>
                    </div>

                    <div style={{ fontSize: '0.85rem', color: '#6e3b0cff', marginBottom: '1rem', padding: '8px', backgroundColor: '#fef2e0ff', borderRadius: '4px' }}>
                        <strong>Stop:</strong> Do <u>not</u> include points paid for services (appraisal, title, notary). Only points paid to <strong>borrow money</strong> are deductible.
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label style={{ fontSize: '0.9rem' }}>Total Points Paid in 2025</label>
                            <input 
                                type="number" 
                                value={data.pointsCalculator?.totalPointsPaid ?? ''} 
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    onDataChange({ target: { name: 'itemizedDeductions.pointsCalculator.totalPointsPaid', value: val } });
                                    
                                    const term = data.pointsCalculator?.loanTermYears || 30;
                                    const isRefi = data.pointsCalculator?.isRefinance || false;
                                    const final = isRefi ? (val / term) : val;
                                    
                                    onDataChange({ target: { name: 'itemizedDeductions.pointsNot1098', value: final.toFixed(2) } });
                                }}
                                placeholder="Total from Settlement Statement" 
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '0.9rem' }}>Loan Term (Years)</label>
                            <input 
                                type="number" 
                                value={data.pointsCalculator?.loanTermYears ?? 30}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 30;
                                    onDataChange({ target: { name: 'itemizedDeductions.pointsCalculator.loanTermYears', value: val } });
                                    
                                    const total = data.pointsCalculator?.totalPointsPaid || 0;
                                    const isRefi = data.pointsCalculator?.isRefinance || false;
                                    const final = isRefi ? (total / val) : total;
                                    
                                    onDataChange({ target: { name: 'itemizedDeductions.pointsNot1098', value: final.toFixed(2) } });
                                }}
                            />
                        </div>
                    </div>

                    <div className="form-group checkbox-group">
                        <input 
                            type="checkbox" 
                            id="isRefinance"
                            checked={data.pointsCalculator?.isRefinance || false}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                const syntheticEvent = { target: { name: 'itemizedDeductions.pointsCalculator.isRefinance', value: isChecked, type: 'checkbox' } };
                                onDataChange(syntheticEvent);

                                const total = data.pointsCalculator?.totalPointsPaid || 0;
                                const term = data.pointsCalculator?.loanTermYears || 30;
                                const final = isChecked ? (total / term) : total;
                                
                                onDataChange({ target: { name: 'itemizedDeductions.pointsNot1098', value: final.toFixed(2) } });
                            }}
                        />
                        <label htmlFor="isRefinance" style={{ fontSize: '0.9rem' }}>
                            This was a <strong>Refinance</strong> (Deduction is spread over life of loan)
                        </label>
                    </div>

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label style={{ fontWeight: 'bold' }}>Deductible Amount for 2025 (Line 8c)</label>
                        <div className="input-with-icon" style={{ position: 'relative' }}>
                            <input 
                                type="number" 
                                name="itemizedDeductions.pointsNot1098" 
                                value={data.pointsNot1098 ?? ''} 
                                onChange={onDataChange} 
                                style={{ fontWeight: 'bold', borderColor: '#e9910eff', backgroundColor: '#fff7ed' }}
                            />
                            <Link size={14} style={{ position: 'absolute', right: '10px', top: '12px', color: '#e97f0eff' }} />
                        </div>
                    </div>
                </div>

                {/* Investment Interest (Line 9) */}
                <div className="form-group" style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                    <div className="header-title-with-help">
                        <label style={{ color: '#9a3412', fontWeight: '600' }}>Investment Interest (Line 9)</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Interest paid on money borrowed to purchase taxable investments (margin interest).">
                            <HelpCircle size={16} color="#9a3412" />
                        </div>
                    </div>
                    
                    <div className="input-with-icon" style={{ position: 'relative', marginTop: '5px' }}>
                        <input 
                            type="number" 
                            name="itemizedDeductions.investmentInterest"
                            value={data.investmentInterest ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0.00"
                            style={{ borderColor: '#e9660eff' }}
                        />
                    </div>

                    {(parseFloat(data.investmentInterest) > investmentIncome) && (
                        <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#b45309', backgroundColor: '#fffbeb', padding: '8px', borderRadius: '4px', border: '1px solid #fcd34d' }}>
                            <AlertCircle size={14} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
                            <strong>Limit Reached:</strong> Your interest expense (${parseFloat(data.investmentInterest).toFixed(2)}) exceeds your Net Investment Income (${investmentIncome.toFixed(2)}). 
                            <br/>
                            You must file <strong>Form 4952</strong>. Your deduction on Line 9 is generally limited to your Net Investment Income.
                        </div>
                    )}
                </div>

                {/* 2. Casualty & Theft Losses (Line 15) */}
                <h5 style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.95rem', borderTop: '1px dashed #e1d7cbff', paddingTop: '1rem' }}>Casualty & Theft Losses (Line 15)</h5>
                
                <div className="form-group" style={{ backgroundColor: '#fff1f2', padding: '1rem', borderRadius: '6px', border: '1px solid #fecdd3' }}>
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label style={{ color: '#be123c', fontWeight: '600' }}>Federally Declared Disasters Only</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="You can only deduct losses from a federally declared disaster (e.g., hurricane, wildfire).">
                            <HelpCircle size={16} color="#be123c" />
                        </div>
                    </div>

                    <div className="form-group checkbox-group" style={{ marginBottom: '1rem' }}>
                        <input 
                            type="checkbox" 
                            id="isFederallyDeclared"
                            name="itemizedDeductions.casualtyCalculator.isFederallyDeclared"
                            checked={data.casualtyCalculator?.isFederallyDeclared || false}
                            onChange={(e) => {
                                const syntheticEvent = { 
                                    target: { name: 'itemizedDeductions.casualtyCalculator.isFederallyDeclared', value: e.target.checked, type: 'checkbox' } 
                                };
                                onDataChange(syntheticEvent);
                            }}
                        />
                        <label htmlFor="isFederallyDeclared" style={{ fontSize: '0.9rem', color: '#881337' }}>
                            This loss occurred in a Federally Declared Disaster Area.
                        </label>
                    </div>

                    {data.casualtyCalculator?.isFederallyDeclared && (
                        <>
                            <div className="form-row">
                                <div className="form-group">
                                    <label style={{ fontSize: '0.9rem' }}>Total Loss (FMV or Basis)</label>
                                    <input 
                                        type="number" 
                                        name="itemizedDeductions.casualtyCalculator.totalLoss"
                                        value={data.casualtyCalculator?.totalLoss ?? ''} 
                                        onChange={(e) => onDataChange({ target: { name: 'itemizedDeductions.casualtyCalculator.totalLoss', value: parseFloat(e.target.value) } })}
                                        placeholder="Total value lost"
                                    />
                                </div>
                                <div className="form-group">
                                    <label style={{ fontSize: '0.9rem' }}>Insurance Reimbursement</label>
                                    <input 
                                        type="number" 
                                        name="itemizedDeductions.casualtyCalculator.insuranceReimbursement"
                                        value={data.casualtyCalculator?.insuranceReimbursement ?? ''} 
                                        onChange={(e) => onDataChange({ target: { name: 'itemizedDeductions.casualtyCalculator.insuranceReimbursement', value: parseFloat(e.target.value) } })}
                                        placeholder="Amount paid by insurance"
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #f0e8e2ff', fontSize: '0.85rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span>Net Loss (minus $100 floor):</span>
                                    <span>${Math.max(0, (parseFloat(data.casualtyCalculator?.totalLoss || 0) - parseFloat(data.casualtyCalculator?.insuranceReimbursement || 0) - 100)).toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#be123c' }}>
                                    <span>Minus 10% of AGI Threshold:</span>
                                    <span>- ${(agi * 0.10).toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #f0e9e2ff', paddingTop: '4px' }}>
                                    <span>Deductible Amount (Line 15):</span>
                                    <span>
                                        ${Math.max(0, (parseFloat(data.casualtyCalculator?.totalLoss || 0) - parseFloat(data.casualtyCalculator?.insuranceReimbursement || 0) - 100) - (agi * 0.10)).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            
                            <small style={{ display: 'block', marginTop: '8px', color: '#881337' }}>
                                <strong>Note:</strong> If this is a "Net Qualified Disaster Loss" (Form 4684 Line 15), do not enter it here. Enter it in "Other Itemized Deductions" (Line 16) below.
                            </small>
                        </>
                    )}
                </div>

                {/* 3. Other Itemized Deductions (Line 16) */}
                <h5 style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.95rem', borderTop: '1px dashed #e1d1cbff', paddingTop: '1rem' }}>Other Itemized Deductions (Line 16)</h5>
                
                {/* A. Gambling Losses */}
                <div className="form-group" style={{ backgroundColor: '#fff7ed', padding: '10px', borderRadius: '6px', border: '1px solid #fed7aa', marginBottom: '10px' }}>
                    <div className="header-title-with-help">
                        <label style={{ color: '#9a3412', fontWeight: '600' }}>Gambling Losses</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Deductible only up to the amount of gambling winnings you reported on Schedule 1.">
                            <HelpCircle size={16} color="#9a3412" />
                        </div>
                    </div>
                    <div className="input-with-icon" style={{ position: 'relative', marginTop: '5px' }}>
                        <input 
                            type="number" 
                            name="itemizedDeductions.line16Details.gamblingLosses"
                            value={data.line16Details?.gamblingLosses ?? ''} 
                            onChange={onDataChange}
                            placeholder="Total losses (tickets, etc.)"
                        />
                    </div>
                    {(parseFloat(data.line16Details?.gamblingLosses) > 0) && (
                        <small style={{ display: 'block', marginTop: '5px', color: '#6e330cff' }}>
                            Deductible Amount: <strong>${Math.min(parseFloat(data.line16Details?.gamblingLosses || 0), parseFloat(formData?.gamblingWinnings || 0) || 0).toFixed(2)}</strong> (Limited to Winnings)
                        </small>
                    )}
                </div>

                {/* B. Net Qualified Disaster Loss */}
                <div className="form-group">
                    <label>Net Qualified Disaster Loss (Form 4684, Line 15)</label>
                    <input 
                        type="number" 
                        name="itemizedDeductions.line16Details.netQualifiedDisasterLoss"
                        value={data.line16Details?.netQualifiedDisasterLoss ?? ''} 
                        onChange={onDataChange}
                        placeholder="Do NOT include Line 15 amounts here"
                    />
                    <small>Only for qualified disasters. Do not duplicate amounts from Line 15 above.</small>
                </div>

                {/* C. Other Specific Items */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Impairment-Related Work Expenses</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.line16Details.impairmentWorkExpenses"
                            value={data.line16Details?.impairmentWorkExpenses ?? ''} 
                            onChange={onDataChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>Repayment under Claim of Right</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.line16Details.claimOfRightRepayment"
                            value={data.line16Details?.claimOfRightRepayment ?? ''} 
                            onChange={onDataChange}
                            placeholder="If over $3,000"
                        />
                    </div>
                </div>

                {/* D. Catch-all */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Other Allowable Expenses</label>
                        <input 
                            type="text" 
                            name="itemizedDeductions.line16Details.otherMiscDescription"
                            value={data.line16Details?.otherMiscDescription ?? ''} 
                            onChange={onDataChange}
                            placeholder="Description (e.g. Bond Premium)"
                        />
                    </div>
                    <div className="form-group">
                        <label>Amount</label>
                        <input 
                            type="number" 
                            name="itemizedDeductions.line16Details.otherMiscAmount"
                            value={data.line16Details?.otherMiscAmount ?? ''} 
                            onChange={onDataChange}
                        />
                    </div>
                </div>

            </div>
        )}
      </div>

      {/* --- Line 18: Election to Itemize --- */}
      <div className="dependent-card" style={{ marginTop: '1.5rem', border: '1px solid #e1d6cbff' }}>
        <div className="form-group checkbox-group">
            <input 
                type="checkbox" 
                id="forceItemized" 
                name="itemizedDeductions.forceItemized" 
                checked={data.forceItemized || false} 
                onChange={onDataChange} 
            />
            <label htmlFor="forceItemized" style={{ fontWeight: '600', color: '#554133ff' }}>
                Elect to itemize deductions even if less than standard deduction (Line 18)
            </label>
        </div>
        <small style={{ display: 'block', marginTop: '5px', marginLeft: '25px', color: '#8b7b64ff' }}>
            Check this box if you want to force Schedule A, usually to claim itemized deductions on your state return.
        </small>
        
        {/* Show comparison if forced */}
        {data.forceItemized && (totalItemized < standardDeduction) && (
            <div style={{ marginTop: '10px', marginLeft: '25px', padding: '8px', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '4px', color: '#9a3412', fontSize: '0.9rem' }}>
                <AlertCircle size={14} style={{ marginRight: '5px' }}/>
                <strong>Warning:</strong> You are choosing a lower deduction (${totalItemized.toLocaleString()}) than your Standard Deduction (${standardDeduction.toLocaleString()}). This will increase your federal tax.
            </div>
        )}
      </div>

    </div>
  );
}

export default ItemizedDeductions;